import MuleBadge from './MuleBadge';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ClockIcon,
  QueueListIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import {
  TruckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CubeIcon,
  TagIcon,
  UserIcon,
  ArchiveBoxIcon,
  MapIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentListIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import searchService, { GlobalSearchResult, GlobalSearchResults } from '../services/search.service';
import { activityTracker } from '../services/activity.service';
import { parsePastedSearchTerms } from '../utils/parsePastedSearchTerms';
import {
  loadRecentSearches,
  addRecentSearch,
  clearRecentSearches,
  bulkTerms,
  RecentSearch,
} from '../utils/recentSearches';
import { config } from '../config/config';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { DriverDispatchHistoryModal } from '../pages/dispatching/DriverDispatchHistory';
import DraggableModal from './ui/DraggableModal';
import CustomOrderSearchForm from './CustomOrderSearchForm';
import CustomOrderFilterForm from './CustomOrderFilterForm';

interface GlobalSearchProps {
  className?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ className }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResults>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Distinguish "the search failed" from "nothing matched" — a backend blip
  // must not read as "that PRO doesn't exist" (2026-08-25 sweep).
  const [searchFailed, setSearchFailed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recents, setRecents] = useState<RecentSearch[]>(() => loadRecentSearches());
  const [isRecentsOpen, setIsRecentsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const recentsRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  // Magnifier menu (Custom Search / Custom Filter) and the modals it launches.
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const [isCustomSearchOpen, setIsCustomSearchOpen] = useState(false);
  const [isCustomFilterOpen, setIsCustomFilterOpen] = useState(false);
  const magnifierRef = useRef<HTMLButtonElement>(null);
  // The Custom Search modal's Search action, published by the form so Alt+S can
  // fire it. Null whenever the button itself is disabled.
  const customSearchSubmitRef = useRef<(() => void) | null>(null);
  const searchMenuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // The menu hangs off the magnifier itself, not the whole field, so it lines up
  // under the icon rather than spanning the input.
  const updateMenuPos = useCallback(() => {
    const rect = magnifierRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 8, left: rect.left });
  }, []);

  // Update dropdown position based on the search input's bounding rect
  const updateDropdownPos = useCallback(() => {
    if (searchRef.current) {
      const rect = searchRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Store legacy CMS URL for use in render functions
  const legacyCmsUrl = config.LEGACY_CMS_URL;

  // A bulk search has no inline preview — it goes straight to the batch results
  // page. Shared by the multi-value paste handler and recents replay.
  const runBulkSearch = (joinedTerms: string, source: 'multi-paste' | 'recent' = 'multi-paste') => {
    const count = joinedTerms.split(',').filter((t) => t.trim()).length;
    activityTracker.track({
      event_type: 'search',
      target: 'global-search',
      detail: `${source} (${count} terms)`,
    });
    setRecents(addRecentSearch(joinedTerms, 'bulk'));
    navigate(`/searchresults?q=${encodeURIComponent(joinedTerms)}&multi=true`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setIsRecentsOpen(false);
    setSelectedIndex(-1);
  };

  // The typed query itself as a multi-value list, or null when it is a single
  // term. A user who pastes a PRO, types a comma, and pastes the next one has
  // built a list by hand — the field holds "A,B" and must be searched as two
  // values, not as one string that matches neither.
  const multiTermsOf = (text: string): string[] | null => {
    const terms = parsePastedSearchTerms(text);
    return terms && terms.length > 1 ? terms : null;
  };
  const queryMultiTerms = useMemo(() => multiTermsOf(query), [query]);

  // Detect multi-value paste (commas, newlines, or a space-separated id list).
  // A pasted LIST replaces whatever was typed before it. A pasted SINGLE value
  // is judged on what the FIELD will contain after the paste, so
  // "5064279," + pasted "5064281" is recognised as two PROs.
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    const input = e.currentTarget;
    const current = input.value ?? '';
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;
    const combined = current.slice(0, start) + pastedText + current.slice(end);
    const terms = multiTermsOf(pastedText) ?? parsePastedSearchTerms(combined);
    // Not a recognized multi-value list -> let the browser paste normally.
    if (!terms) return;
    e.preventDefault();
    if (terms.length > 1) {
      runBulkSearch(terms.join(','));
      return;
    }
    // Single value after splitting - just set it as query
    if (terms.length === 1) {
      setQuery(terms[0]);
    }
  };

  // Close dropdowns when clicking outside (the search input and both portaled panels)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      if (recentsRef.current?.contains(target)) return;
      if (searchMenuRef.current?.contains(target)) return;
      setIsOpen(false);
      setIsRecentsOpen(false);
      setIsSearchMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reposition whichever panel is open on scroll/resize
  useEffect(() => {
    if (!isOpen && !isRecentsOpen) return;
    updateDropdownPos();
    window.addEventListener('scroll', updateDropdownPos, true);
    window.addEventListener('resize', updateDropdownPos);
    return () => {
      window.removeEventListener('scroll', updateDropdownPos, true);
      window.removeEventListener('resize', updateDropdownPos);
    };
  }, [isOpen, isRecentsOpen, updateDropdownPos]);

  // Same for the magnifier menu, which tracks the icon instead of the field.
  useEffect(() => {
    if (!isSearchMenuOpen) return;
    updateMenuPos();
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [isSearchMenuOpen, updateMenuPos]);

  // Escape closes the menu even when focus has moved off the button.
  useEffect(() => {
    if (!isSearchMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSearchMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSearchMenuOpen]);

  // Helper function to get icon for result type
  const getIconForType = (type: GlobalSearchResult['type']): React.ComponentType<{ className?: string }> => {
    switch (type) {
      case 'pro':
        return CubeIcon;
      case 'template':
        return ClipboardDocumentListIcon;
      case 'driver':
        return TruckIcon;
      case 'customer':
        return DocumentTextIcon;
      case 'dispatcher':
        return UserIcon;
      case 'driver_orders':
        return UserGroupIcon;
      case 'movetype':
        return TagIcon;
      case 'container':
        return ArchiveBoxIcon;
      case 'chassis':
        return WrenchScrewdriverIcon;
      case 'bol':
        return IdentificationIcon;
      case 'location':
        return MapPinIcon;
      case 'quote':
        return DocumentTextIcon;
      default:
        return DocumentTextIcon;
    }
  };

  // Perform search when query changes
  useEffect(() => {
    const performSearch = async () => {
      if (query.trim().length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      // A hand-built list has no single-term preview: the batch results page
      // is the destination. Open the dropdown with a "Search N values" hint
      // instead of asking the backend to match the comma-joined string.
      if (queryMultiTerms) {
        searchAbortRef.current?.abort();
        setResults([]);
        setSearchFailed(false);
        setIsLoading(false);
        setIsOpen(true);
        setIsRecentsOpen(false);
        return;
      }

      // Abort any previous search request
      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;

      setIsLoading(true);
      setIsOpen(true);
      setIsRecentsOpen(false);

      try {
        // Search current/open orders only — closed orders are reachable via the
        // "Search Closed Orders" link shown in the no-results state.
        // ordersAsPros: a container or chassis number identifies an ORDER, so
        // those matches come back as PRO rows in the "PRO Numbers" group rather
        // than their own CONTAINERS / CHASSIS groups. One container carried by
        // two orders used to list as two identically-labeled container rows,
        // while a third order matching the same value by booking number sat in
        // a separate group — now every matching order appears once, by pro.
        const currentResults = await searchService.unifiedGlobalSearch(
          query, 5, controller.signal, 'current', undefined, false, undefined, true,
        );
        if (controller.signal.aborted) return;
        setSearchFailed(false);
        setResults(currentResults);
        // Remember anything that actually matched; a run of keystrokes collapses
        // into one entry, so this costs at most one slot per lookup.
        if (currentResults.length > 0) {
          setRecents(addRecentSearch(query));
        }
      } catch (error: any) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error('Global search failed:', error);
        setSearchFailed(true);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      performSearch();
    }, 300);

    return () => {
      clearTimeout(debounceTimer);
      searchAbortRef.current?.abort();
    };
  }, [query, queryMultiTerms]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Escape dismisses the recents panel before anything else looks at the results.
    if (e.key === 'Escape' && isRecentsOpen) {
      setIsRecentsOpen(false);
      return;
    }
    // Enter on a typed comma/space list goes straight to the batch search —
    // there is no highlighted single result to pick.
    if (e.key === 'Enter' && queryMultiTerms) {
      e.preventDefault();
      runBulkSearch(queryMultiTerms.join(','));
      return;
    }
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Driver-specific navigation handlers
  const handleDriverMapClick = (unitNumber: string) => {
    navigate(`/fleet-management/live-map?driver=${unitNumber}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleDriverDSMClick = (unitNumber: string) => {
    navigate(`/fleet-management/driver-status-monitor?driver=${unitNumber}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const [historyModalUnit, setHistoryModalUnit] = useState<string | null>(null);

  const handleDriverDispatchHistoryClick = (unitNumber: string) => {
    setHistoryModalUnit(unitNumber);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleResultClick = (result: GlobalSearchResult) => {
    activityTracker.track({
      event_type: 'search',
      target: 'global-search',
      detail: `"${query}" -> ${result.type}:${result.value}`,
    });
    let url = '';

    switch (result.type) {
      case 'pro':
        // Navigate to search results page with PRO drawer open
        navigate(`/searchresults?q=${encodeURIComponent(query)}&pro=${encodeURIComponent(result.value)}`);
        break;
      case 'template':
        // Templates ride the same search-results + PRO drawer flow as 'pro'.
        // (The direct /pro/T… full-page link still works via ProPage.)
        navigate(`/searchresults?q=${encodeURIComponent(query)}&pro=${encodeURIComponent(result.value)}`);
        break;
      case 'quote':
        // Search-results page with the quote editor drawer open (mirrors 'pro').
        navigate(`/searchresults?q=${encodeURIComponent(query)}&quote=${encodeURIComponent(result.value)}`);
        break;
      case 'container':
        // Navigate to search results page with container
        navigate(`/searchresults?q=${encodeURIComponent(result.value)}&pro=${encodeURIComponent(result.data?.pronumber || '')}`);
        break;
      case 'chassis':
        // Navigate to search results page with chassis's associated PRO
        navigate(`/searchresults?q=${encodeURIComponent(result.value)}&pro=${encodeURIComponent(result.data?.pronumber || '')}`);
        break;
      case 'bol':
        // Navigate to search results page with BOL's associated PRO
        navigate(`/searchresults?q=${encodeURIComponent(result.value)}&pro=${encodeURIComponent(result.data?.pronumber || '')}`);
        break;
      case 'driver':
        // Navigate to Live Map and select this driver (default behavior)
        handleDriverMapClick(result.value);
        break;
      case 'driver_orders':
      case 'customer':
      case 'dispatcher': {
        // Aggregate rows — one line summarizing an entity's orders. These used
        // to hand a contlist URL to V1 legacy CMS; they now load that entity's
        // orders as PRO rows on the results page (the PRO TanTable).
        //
        // `q` carries the row's EXACT identity (unit number / custname /
        // dispatcher username), not the typed query — "008" also matched units
        // 9008 and 6008. The page refetches by entity rather than replaying a
        // pro list, so the URL stays short and the grid never goes stale.
        const ordersBy = result.type === 'driver_orders' ? 'driver' : result.type;
        navigate(
          `/searchresults?q=${encodeURIComponent(result.value)}`
          + `&ordersBy=${ordersBy}`
          + `&label=${encodeURIComponent(result.label)}`,
        );
        break;
      }
      case 'movetype': {
        // Still legacy — Move Types is far too broad for the results grid (it's
        // filtered off the results page entirely).
        const proNumbers = result.data?.pronumbers?.slice(0, 50).join(',') || '';
        if (proNumbers) {
          url = `${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${proNumbers}&searchtype=pronumber`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
      }
      case 'location':
        // Navigate to Locations page
        navigate('/data-management/locations');
        break;
    }

    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleOpenAll = () => {
    activityTracker.track({
      event_type: 'search',
      target: 'global-search',
      detail: `"${query}" -> all results`,
    });
    // Navigate to search results page showing all PRO results
    navigate(`/searchresults?q=${encodeURIComponent(query)}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleSearchClosedOrders = () => {
    navigate(`/searchresults?q=${encodeURIComponent(query)}&closed=true`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleViewAllResults = () => {
    navigate(`/searchresults?q=${encodeURIComponent(query)}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleToggleRecents = () => {
    if (isRecentsOpen) {
      setIsRecentsOpen(false);
      return;
    }
    // Re-read on open so terms recorded in another tab show up here too.
    setRecents(loadRecentSearches());
    setIsOpen(false);
    setIsSearchMenuOpen(false);
    updateDropdownPos();
    setIsRecentsOpen(true);
  };

  // Only one panel anchored to the field may be open at a time, so opening the
  // magnifier menu closes the results and recents dropdowns.
  const handleToggleSearchMenu = () => {
    if (isSearchMenuOpen) {
      setIsSearchMenuOpen(false);
      return;
    }
    setIsOpen(false);
    setIsRecentsOpen(false);
    updateMenuPos();
    setIsSearchMenuOpen(true);
  };

  const handleOpenCustomSearch = () => {
    setIsSearchMenuOpen(false);
    setIsCustomSearchOpen(true);
  };

  const handleOpenCustomFilter = () => {
    setIsSearchMenuOpen(false);
    setIsCustomFilterOpen(true);
  };

  // Alt+S runs the Custom Search modal's Search button, matching V1's footer
  // accesskey. The chord exists only while that modal is open.
  //
  // Two things worth knowing. (1) The claim has to live HERE, not inside
  // CustomOrderSearchForm: the form mounts as a DraggableModal descendant, so its
  // effect runs BEFORE the shell's and its layer would sit underneath. GlobalSearch
  // renders the shell, so its effect runs after and wins. (2) Winning makes the
  // shell's own claim inert, so Alt+C has to be re-bound here or keyboard close
  // silently stops working.
  //
  // Search isn't a persist, so the usual rule would leave Alt+S unbound; it's here
  // because V1 users' fingers expect it in this window.
  useAltHotkeys(
    {
      // Null while the button is disabled, so chord and button refuse an empty
      // list identically.
      S: () => customSearchSubmitRef.current?.(),
      C: () => setIsCustomSearchOpen(false),
    },
    undefined,
    { layer: isCustomSearchOpen },
  );

  // Custom Search hands off to the results grid the same way a bulk paste does —
  // the values ride in the URL so the search is shareable and survives a reload.
  // `multi` stays off: this is a single-field search, not the any-facet batch.
  const handleCustomSearch = (
    searchBy: string,
    terms: string[],
    includeDeleted: boolean,
    viewId: string,
  ) => {
    activityTracker.track({
      event_type: 'search',
      target: 'global-search',
      detail: `custom-search ${searchBy} (${terms.length} terms)`,
    });
    setIsCustomSearchOpen(false);
    // Newline-joined, NOT comma-joined: LOT NUMBER values may contain commas, and
    // a comma round-trip would split one value into two. The results page splits
    // `q` on newlines whenever searchBy is present.
    const params = new URLSearchParams({
      q: terms.join('\n'),
      searchBy,
    });
    if (includeDeleted) params.set('includeDeleted', 'true');
    // The chosen View rides the URL alongside the criteria, so the results view
    // stays shareable and reload-safe and a shared link carries the layout too.
    // The numeric id, not the name — renaming a View must not break saved links.
    if (viewId) params.set('view', viewId);
    navigate(`/searchresults?${params.toString()}`);
  };

  // Custom Filter hands the condition JSON to the results grid the same way. It
  // rides in the URL (not router state) so the filtered view survives a reload and
  // can be shared; FILTER_MAX_CONDITIONS keeps the URL bounded.
  const handleCustomFilter = (filterJson: string, includeDeleted: boolean, viewId: string) => {
    activityTracker.track({
      event_type: 'search',
      target: 'global-search',
      detail: 'custom-filter',
    });
    setIsCustomFilterOpen(false);
    const params = new URLSearchParams({ filter: filterJson });
    if (includeDeleted) params.set('includeDeleted', 'true');
    if (viewId) params.set('view', viewId);
    navigate(`/searchresults?${params.toString()}`);
  };

  // Replaying a recent search does whatever the original did: a single term
  // behaves exactly like typing it (the search effect takes over and opens the
  // normal results dropdown), while a bulk list re-runs the batch search.
  const handleRecentClick = (entry: RecentSearch) => {
    if (entry.kind === 'bulk') {
      runBulkSearch(entry.term, 'recent');
      return;
    }
    setIsRecentsOpen(false);
    setSelectedIndex(-1);
    setQuery(entry.term);
    inputRef.current?.focus();
  };

  const handleClearRecents = () => {
    clearRecentSearches();
    setRecents([]);
  };

  const getTypeColor = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'pro':
        return 'text-purple-600 dark:text-purple-400';
      case 'template':
        return 'text-amber-600 dark:text-amber-400';
      case 'driver':
        return 'text-green-600 dark:text-green-400';
      case 'customer':
        return 'text-blue-600 dark:text-blue-400';
      case 'dispatcher':
        return 'text-orange-600 dark:text-orange-400';
      case 'driver_orders':
        return 'text-teal-600 dark:text-teal-400';
      case 'movetype':
        return 'text-indigo-600 dark:text-indigo-400';
      case 'container':
        return 'text-cyan-600 dark:text-cyan-400';
      case 'chassis':
        return 'text-amber-600 dark:text-amber-400';
      case 'bol':
        return 'text-pink-600 dark:text-pink-400';
      case 'location':
        return 'text-rose-600 dark:text-rose-400';
      case 'quote':
        return 'text-blue-700 dark:text-blue-300';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Group results by their group field
  const groupedResults = results.reduce((acc, result) => {
    const groupName = result.group;
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(result);
    return acc;
  }, {} as Record<string, GlobalSearchResult[]>);

  // "Open All PROs" and "View all results" both navigate to the same
  // /searchresults?q=<query>. So when every result is a PRO, the footer is a
  // second button doing exactly what the first one does — hide it. Keep it
  // whenever the dropdown holds something Open All doesn't speak for (drivers,
  // locations, quotes, templates...), or when there's no Open All button.
  const proResultCount = results.filter(r => r.type === 'pro').length;
  const showViewAllResults = !(proResultCount > 1 && proResultCount === results.length);
  // #33651 — what "Open All PROs" will actually open, which is NOT what the
  // dropdown is showing: the typeahead asks for 5 per category while the results
  // page it navigates to asks for the ceiling. BOL 2338085930 listed 5 of its 8
  // orders and promised 5, then opened all 8. The backend sends the exact total
  // when it truncated; without it the rows are the whole set. Deliberately kept
  // apart from proResultCount above, which counts what's ON SCREEN and drives
  // the layout decisions. The backend caps its total at the per-category ceiling
  // it will actually return, so this number is always openable as stated.
  const proOpenAllCount = results.proTotal ?? proResultCount;

  /**
   * The way to the closed + soft-deleted set, in BOTH dropdown states.
   *
   * It used to render only when the search came up empty, which made the closed /
   * deleted set unreachable whenever a query matched anything at all: searching
   * "81407" matches quote 81407-1, so the dropdown had results, so the link never
   * appeared — and deleted template T81407 could not be reached from the header at
   * all. ("View all results" doesn't reach it either: it re-runs the SAME
   * current-scope search, just uncapped.) Having results says nothing about whether
   * the record you actually wanted is a closed or deleted one.
   */
  const closedSearchLink = (
    <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
      <button
        onClick={handleSearchClosedOrders}
        className="w-full text-left text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center justify-between"
      >
        <span>Search Closed &amp; Deleted Orders and Templates</span>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        {/* Magnifier doubles as the Custom Search / Custom Filter menu trigger */}
        <button
          ref={magnifierRef}
          type="button"
          onClick={handleToggleSearchMenu}
          aria-label="Search options"
          aria-haspopup="menu"
          aria-expanded={isSearchMenuOpen}
          title="Search options"
          className={`absolute inset-y-0 left-0 z-10 flex w-10 items-center pl-3 rounded-l-md transition-colors ${
            isSearchMenuOpen
              ? 'text-gray-600 dark:text-gray-200'
              : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        >
          <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.trim().length >= 2 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          className={`block w-full rounded-md border-0 bg-white dark:bg-gray-800 py-2 pl-10 ${query ? 'pr-20' : 'pr-11'} text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500 sm:text-sm sm:leading-6`}
          placeholder="Search PROs, bookings, containers, chassis, BOLs, drivers..."
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute inset-y-0 right-9 flex items-center pr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
        {/* Recent-searches toggle: square, divided off the right edge of the field */}
        <button
          type="button"
          onClick={handleToggleRecents}
          aria-label="Recent searches"
          aria-expanded={isRecentsOpen}
          title="Recent searches"
          className={`absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md border-l border-gray-300 dark:border-gray-700 transition-colors ${
            isRecentsOpen
              ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200'
              : 'text-gray-400 hover:bg-highlight hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        >
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Recent Searches Panel - portaled like the results dropdown, same anchor */}
      {isRecentsOpen && createPortal(
        <div
          ref={recentsRef}
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          className="rounded-md bg-panel shadow-lg ring-1 ring-black ring-opacity-5 max-h-96 overflow-auto">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Recent Searches
            </span>
            {recents.length > 0 && (
              <button
                type="button"
                onClick={handleClearRecents}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Clear
              </button>
            )}
          </div>
          {recents.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted">
              No recent searches yet
            </div>
          ) : (
            <ul className="py-1">
              {recents.map((entry) => {
                const isBulk = entry.kind === 'bulk';
                const Icon = isBulk ? QueueListIcon : ClockIcon;
                return (
                  <li key={`${entry.kind}:${entry.term}`}>
                    <button
                      type="button"
                      onClick={() => handleRecentClick(entry)}
                      title={entry.term}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                    >
                      <Icon
                        className={`h-4 w-4 flex-shrink-0 ${isBulk ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400'}`}
                        aria-hidden="true"
                      />
                      <span className="flex-1 min-w-0 text-sm text-body truncate">
                        {entry.term}
                      </span>
                      {isBulk && (
                        <span className="flex-shrink-0 rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                          {bulkTerms(entry).length} terms
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>,
        document.body
      )}

      {/* Search Results Dropdown - Portaled to body to escape overflow:hidden */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          className="rounded-md bg-panel shadow-lg ring-1 ring-black ring-opacity-5 max-h-96 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-muted">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div className="py-1">
              {/* Open All Button - Show if there are multiple PRO results */}
              {proResultCount > 1 && (
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleOpenAll}
                    className="w-full px-3 py-2 text-sm font-medium text-accent-fg bg-accent hover:bg-accent-hover rounded-md transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open All PROs ({proOpenAllCount})
                  </button>
                </div>
              )}
              {Object.entries(groupedResults).map(([groupName, groupResults]) => (
                <div key={groupName}>
                  {/* Group Header */}
                  <div className="px-3 py-2 text-xs font-semibold text-muted uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                    {groupName}
                  </div>
                  {/* Group Results */}
                  <ul>
                    {groupResults.map((result) => {
                      const Icon = getIconForType(result.type);
                      const globalIndex = results.indexOf(result);
                      const isSelected = globalIndex === selectedIndex;

                      // Special rendering for driver results with action buttons
                      if (result.type === 'driver') {
                        return (
                          <li key={`${result.type}-${result.value}`}>
                            <div
                              onMouseEnter={() => setSelectedIndex(globalIndex)}
                              className={`w-full px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-start gap-3 transition-colors ${
                                isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                              }`}
                            >
                              <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${getTypeColor(result.type)}`} />
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${
                                  isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-body'
                                }`}>
                                  {result.label}<MuleBadge isMule={result.data?.isMule} />
                                </div>
                                {result.description && (
                                  <div className={`text-xs truncate mt-0.5 ${
                                    isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-muted'
                                  }`}>
                                    {result.description}
                                  </div>
                                )}
                              </div>
                              {/* Driver action buttons */}
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDriverMapClick(result.value);
                                  }}
                                  className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 transition-colors"
                                  title="View on Map"
                                >
                                  <MapIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDriverDispatchHistoryClick(result.value);
                                  }}
                                  className="p-1.5 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-600 dark:text-amber-400 transition-colors"
                                  title="View Driver Dispatch History"
                                >
                                  <ClipboardDocumentListIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDriverDSMClick(result.value);
                                  }}
                                  className="px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors"
                                  title="View in Driver Status Monitor"
                                >
                                  DSM
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      }

                      // Default rendering for non-driver results
                      return (
                        <li key={`${result.type}-${result.value}`}>
                          <button
                            onClick={() => handleResultClick(result)}
                            onMouseEnter={() => setSelectedIndex(globalIndex)}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-start gap-3 transition-colors ${
                              isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${getTypeColor(result.type)}`} />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium truncate ${
                                isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-body'
                              }`}>
                                {result.label}{result.type === 'driver_orders' && <MuleBadge isMule={result.data?.isMule} />}
                              </div>
                              {result.description && (
                                <div className={`text-xs truncate mt-0.5 ${
                                  isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-muted'
                                }`}>
                                  {result.description}
                                </div>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {/* View All Results Link — redundant when Open All PROs covers
                  every result (same destination), so it's hidden there. */}
              {showViewAllResults && (
              <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleViewAllResults}
                  className="w-full text-left text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center justify-between"
                >
                  <span>View all results</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              )}
              {closedSearchLink}
            </div>
          ) : (
            <>
              {queryMultiTerms ? (
                <div className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => runBulkSearch(queryMultiTerms.join(','))}
                    className="w-full text-left text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center justify-between"
                  >
                    <span>Search {queryMultiTerms.length} values</span>
                    <span className="text-xs text-muted">Enter</span>
                  </button>
                </div>
              ) : (
                <>
                  <div className="px-4 py-3 text-sm text-muted">
                    {searchFailed
                      ? 'Search is unavailable right now — the request failed. Try again in a moment.'
                      : `No results found for "${query}"`}
                  </div>
                  {closedSearchLink}
                </>
              )}
            </>
          )}
        </div>,
        document.body
      )}

      {/* Magnifier menu — portaled so the header's stacking context can't clip it */}
      {isSearchMenuOpen && createPortal(
        <div
          ref={searchMenuRef}
          role="menu"
          aria-label="Search options"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="min-w-[13rem] rounded-md bg-panel py-1 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleOpenCustomSearch}
            className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-body hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            Custom Search
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleOpenCustomFilter}
            className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-body hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <FunnelIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            Custom Filter
          </button>
        </div>,
        document.body
      )}

      <DraggableModal
        isOpen={isCustomSearchOpen}
        onClose={() => setIsCustomSearchOpen(false)}
        title="Custom Search"
        width={720}
        topOffset={250}
      >
        <CustomOrderSearchForm
          onSearch={handleCustomSearch}
          onCancel={() => setIsCustomSearchOpen(false)}
          submitRef={customSearchSubmitRef}
        />
      </DraggableModal>

      {/* Wider than Custom Search: a condition row needs ~600px before the value
          input starts shrinking (field w-80 + operator w-48 + value + remove). */}
      <DraggableModal
        isOpen={isCustomFilterOpen}
        onClose={() => setIsCustomFilterOpen(false)}
        title="Custom Filter"
        width={980}
        topOffset={250}
      >
        <CustomOrderFilterForm
          onApply={handleCustomFilter}
          onCancel={() => setIsCustomFilterOpen(false)}
        />
      </DraggableModal>

      <DriverDispatchHistoryModal
        isOpen={historyModalUnit !== null}
        unitNumber={historyModalUnit ?? ''}
        onClose={() => setHistoryModalUnit(null)}
      />
    </div>
  );
};

export default GlobalSearch;
